import React, { Fragment, useState, useRef, useEffect, useLayoutEffect, useImperativeHandle, useContext } from 'react';
import _merge from 'lodash/merge';
import { useTransition, animated } from 'react-spring';
import Tokens from '@nokia-csf-uxr/freeform-design-tokens';
import { getContext, useCombinedRefs, isAnimationDisabled } from 'common';
import { getEasing, getDuration } from 'common/parse-tokens';
import { LabelContext } from 'components/Label';
import List, { ListProps } from 'components/List';
import { getMenuMaxHeight } from 'common/MenuDimensionCalc';
import { CONSTANTS } from 'common/ListKeyboardNav';
import Menu, { MenuRef, MenuProps } from 'components/Menu';
import KEYCODES from 'common/KeyCodes';
import {
  SECONDARY_GLOBAL_BASE as DEFAULT_SECONDARY_GLOBAL_BASE,
  SECONDARY_GLOBAL_BACKGROUND as DEFAULT_SECONDARY_GLOBAL_BACKGROUND,
} from 'components/styles/global-colors/global-colors-default-theme';
import {
  SECONDARY_GLOBAL_BASE as DARK_SECONDARY_GLOBAL_BASE,
  SECONDARY_GLOBAL_BACKGROUND as DARK_SECONDARY_GLOBAL_BACKGROUND,
} from 'components/styles/global-colors/global-colors-dark-theme';
import {
  SECONDARY_GLOBAL_BASE as AAA_DEFAULT_SECONDARY_GLOBAL_BASE,
  SECONDARY_GLOBAL_BACKGROUND as AAA_DEFAULT_SECONDARY_GLOBAL_BACKGROUND,
} from 'components/styles/global-colors/global-colors-aaa-default-theme';
import {
  SECONDARY_GLOBAL_BASE as AAA_DARK_SECONDARY_GLOBAL_BASE,
  SECONDARY_GLOBAL_BACKGROUND as AAA_DARK_SECONDARY_GLOBAL_BACKGROUND,
} from 'components/styles/global-colors/global-colors-aaa-dark-theme';
import { sameWidth, isToggleMenuKeyPressed } from './common/config';
import SelectItemErrorMessage from './SelectItemErrorMessage';
import SelectItemButton from './SelectItemButton';

import {
  toggleMenu,
  handleFocus,
  handleBlur,
  handleMenuCloseCallback,
  handleListKeyDown,
  handleMouseOver,
  handleMouseLeave,
  scrollToSelectedItem,
} from './common/functions';
import Styled from './styles';

const { ESCAPE, ARROW_DOWN } = KEYCODES;
const { LIST_ITEM_SELECTED } = CONSTANTS;

// Dropdown List placement. The SelectItem dropdown List is positioned below the component
const defaultPositioningOptions = {
  placement: 'bottom',
  modifiers: [
    { name: 'offset', options: { offset: [0, 4] } },
    sameWidth,
  ],
};

const DEFAULT_TRANSITION = immediate => ({
  from: { position: 'absolute' as const, opacity: 0 },
  enter: { opacity: 1 },
  leave: { opacity: 0 },
  config: {
    duration: getDuration(Tokens.SPEED.SPEED_NORMAL),
    easing: getEasing(Tokens.TRANSITION.TRANSITION_DECELERATE)
  },
  immediate
});
const ELEVATION_INDEX = 8;
const ELEVATION_DARK = false;

const SelectItemBasicContext = React.createContext<any>({});

export interface SelectItemBasicProps extends React.ComponentPropsWithoutRef<'div'> {
  /** The items in the dropdown List. Consider using SelectListItem and SelectGroupHeading. */
  children: React.ReactNode;
  /** Allow multiple selection of list items. */
  multiSelect?: boolean;
  /** Controls dropdown List open/close state. */
  isOpen?: boolean;
  /** Function called when the dropdown List requests to be opened.
  * @param {object} event The event source of the callback.
  */
  onOpen?: (args: React.MouseEvent | React.KeyboardEvent) => void;
  /** Function called when dropdown List requests to be closed.
  * @param {object} event The event source of the callback.
  */
  onClose?: (args: React.MouseEvent | React.KeyboardEvent) => void;
  /** The style variant of the component. */
  variant?: 'outlined' | 'underlined';
  /** If `true`, the component will be disabled. Note: If the `disabled` prop is not set, it will be internally taken from the immediate parent `Label` component, if any. */
  disabled?: boolean;
  /** If `true`, the component will automatically get focus when the page loads. */
  autoFocus?: boolean;
  /**
   * Renders the items that are selected from the dropdown List and any dropdown indicator icon into the SelectItem base area.
   * SelectItem by default uses SelectItemButton component for basic usages.
   * You can implement your own component to replace it. Please note that your component needs
   * to support and handle SelectItemButton props. Please refer to SelectItemButton for the property definitions.
   * e.g. props => <YourCustomItem {...props} />
  */
  renderSelectItemBase?(...args: unknown[]): React.ReactNode;
  /** If `true`, the SelectItem will be rendered in the error state. */
  error?: boolean;
  /** If set, the errorMessage will be displayed in the error state message area. `error` property must be true for errorMessage to display.
   *  `Typography` component can be used to format the error message. */
  errorMessage?: React.ReactNode;
  /** If `true`, the max-width will be `50rem`. */
  maxWidth?: boolean;
  /** When true, the text in the dropdown list will be truncated with ellipsis if it exceeds the container width. Otherwise the List will have
   * a horizontal scroll bar. */
  truncateListText?: boolean;
  /** Dropdown List properties. See List component documentation for details. */
  listProps?: Partial<ListProps>;
  /** Menu properties. See Menu component documentation for full details. */
  menuProps?: Partial<MenuProps>;
  /** Control animation of dropdown list. This uses React-spring transitions config. See https://react-spring.io for more information. */
  animationProps?: any;
  /** If true, disable all motion animation for this component.  This property will override any global disableAnimation setting. */
  disableAnimation?: boolean;
  /** @ignore */
  style?: React.CSSProperties;
  /** @ignore */
  onClick?: React.MouseEventHandler;
  /** @ignore */
  onKeyDown?: React.KeyboardEventHandler;
  /** @ignore */
  onMouseOver?: React.MouseEventHandler;
  /** @ignore */
  onMouseLeave?: React.MouseEventHandler;
  /** @ignore */
  onFocus?: React.FocusEventHandler;
  /** @ignore */
  onBlur?: React.FocusEventHandler;
  /** To override the theme please refer to the "Readme" tab to learn more. */
  theme?: any,
}

export type SelectItemBasicHandle = {
  selectItemRef: HTMLElement,
  menuRef: React.RefObject<MenuRef>,
  positionMenu: (any) => void
};
/** SelectItemBasic is a form input element that is used for collecting user provided information from a list of options.
 * To re-position dropdown Menu on changes to children or target element, please use `positionMenu` callback using component ref.
 * The forwarded ref is available as 'selectItemRef'.
 * */
const SelectItemBasic = React.forwardRef<SelectItemBasicHandle, SelectItemBasicProps>((props: SelectItemBasicProps, ref) => getContext(({ RTL, theme, disableAnimation }) => {
  const {
    style,
    isOpen,
    children,
    multiSelect,
    autoFocus,
    disabled,
    error,
    variant,
    maxWidth,
    errorMessage,
    onOpen,
    onClose,
    truncateListText,
    listProps,
    menuProps,
    animationProps,
    disableAnimation: propsdisableAnimation,
    renderSelectItemBase,
    theme: userTheme,
    ...otherProps
  } = props;
  const {
    style: listStyle,
    ...otherListProps
  } = listProps;
  const {
    ref: externalListRef,
    onKeyDown: externalKeyDown,
    ...restOfListProps
  } = otherListProps;
  const {
    style: menuStyle,
    positioningOptions: menuPositionOptions,
    ...otherMenuProps
  } = menuProps;
  const referenceElement = useRef(null);
  const menuRef = useRef<MenuRef>(null);
  const listRef = useRef(null);
  const combinedListRefs = useCombinedRefs(listRef, externalListRef);
  const [focused, setIsFocused] = useState(false);
  const [isHovered, setIsHovered] = useState();
  const [listMaxHeight, setListMaxHeight] = useState(null);
  const [clickedOutside, setClickedOutside] = useState(false);

  const labelContext = useContext(LabelContext);
  const resolvedDisabled = props.disabled !== undefined ? props.disabled : labelContext.disabled;
  const transitions = useTransition(!resolvedDisabled && isOpen, null, animationProps || DEFAULT_TRANSITION(isAnimationDisabled(propsdisableAnimation, disableAnimation)));
  const isDarkTheme = theme.NAME === 'dark';
  const isAAADarkTheme = theme.NAME === 'aaa-dark';
  const isAAADefaultTheme = theme.NAME === 'aaa-default';

  // Includes a hook to make available the positionMenu function of the Menu component to the user.
  // User would call the selectItemRef.current.positionMenu(). All other props will be provided to the wrapper element.
  useImperativeHandle(ref, () => ({
    selectItemRef: referenceElement.current,
    menuRef,
    positionMenu: () => {
      menuRef.current && menuRef.current.positionMenu();
    }
  }));

  useEffect(() => {
    setListMaxHeight(getMenuMaxHeight(referenceElement.current));
  }, [referenceElement.current]);

  useLayoutEffect(() => {
    if (isOpen) {
      menuRef.current && menuRef.current.positionMenu();
    }
  }, [menuProps, listProps, maxWidth, isOpen]);

  useEffect(() => {
    if (isOpen && !resolvedDisabled) {
      const selectedDiv = listRef && listRef.current && listRef.current.querySelector(`.${LIST_ITEM_SELECTED}`);
      selectedDiv && scrollToSelectedItem(selectedDiv);
    }
  }, [isOpen]);

  const handleSelectClick = (event) => {
    const { onClick } = props;
    toggleMenu(event, isOpen, onClose, onOpen);
    !resolvedDisabled && onClick && onClick(event);
  };

  const handleSelectKeyDown = (event) => {
    const { onKeyDown } = props;
    const { key } = event;
    if (isOpen && key === ARROW_DOWN) {
      // arrow into List
      if (listProps.keyboardNavigation === undefined || listProps.keyboardNavigation) {
        listRef && listRef.current && listRef.current.focus({ preventScroll: true });
        event.preventDefault();
      }
    } else if (isToggleMenuKeyPressed(key)) {
      event.stopPropagation();
      event.preventDefault();
      !resolvedDisabled && toggleMenu(event, isOpen, onClose, onOpen);
    } else if (isOpen && key === ESCAPE) {
      onClose(event);
    }
    !resolvedDisabled && onKeyDown && onKeyDown(event);
  };

  const errorMessageContent =
    typeof errorMessage === 'string' ? <SelectItemErrorMessage>{errorMessage}</SelectItemErrorMessage> : errorMessage;

  const resolvedMenuProps = {
    positioningOptions: _merge({}, defaultPositioningOptions, menuPositionOptions),
    ref: menuRef,
    triggerEl: referenceElement,
    usePortal: true,
    ...otherMenuProps,
    onClose: (event) => { handleMenuCloseCallback(event, setClickedOutside, onClose); },
  };

  // theme for TextInput
  let userTextInputTheme = {};
  if (userTheme && userTheme.TEXT_INPUT) {
    userTextInputTheme = variant === 'outlined' ? userTheme.TEXT_INPUT.OUTLINED : userTheme.TEXT_INPUT.UNDERLINED;
  }
  const globalTextInputTheme = variant === 'outlined' ? theme.TEXT_INPUT.OUTLINED : theme.TEXT_INPUT.UNDERLINED;
  // SelectItem background for outline
  const selectTheme = {
    TEXT_INPUT: {
      OUTLINED: {
        background: isDarkTheme
          ? DARK_SECONDARY_GLOBAL_BASE
          : isAAADarkTheme
            ? AAA_DARK_SECONDARY_GLOBAL_BASE
            : isAAADefaultTheme
              ? AAA_DEFAULT_SECONDARY_GLOBAL_BASE
              : DEFAULT_SECONDARY_GLOBAL_BASE,
      },
      UNDERLINED: {
        background: isDarkTheme
          ? DARK_SECONDARY_GLOBAL_BACKGROUND
          : isAAADarkTheme
            ? AAA_DARK_SECONDARY_GLOBAL_BACKGROUND
            : isAAADefaultTheme
              ? AAA_DEFAULT_SECONDARY_GLOBAL_BACKGROUND
              : DEFAULT_SECONDARY_GLOBAL_BACKGROUND,
      },
    },
  };
  const resolvedTextInputTheme = _merge(
    {},
    globalTextInputTheme,
    variant === 'outlined'
      ? selectTheme.TEXT_INPUT.OUTLINED
      : selectTheme.TEXT_INPUT.UNDERLINED,
    userTextInputTheme
  );
  // theme for List
  const listTheme = _merge({}, theme.LIST, userTheme.LIST);
  const resolvedListTheme = { LIST: { ...listTheme } };

  const resolvedBaseProps = {
    disabled: resolvedDisabled,
    autoFocus,
    isOpen,
    focused,
    hovered: isHovered,
    variant,
    clickedOutside,
    multiSelect,
    resolvedListTheme,
    resolvedTextInputTheme,
  };

  return (
    <Fragment>
      <Styled.SelectItemWrapper
        isOpen={isOpen}
        ref={referenceElement}
        RTL={RTL}
        theme={resolvedTextInputTheme}
        aria-expanded={isOpen}
        disabled={resolvedDisabled}
        variant={variant}
        $multiSelect={multiSelect}
        style={style}
        maxWidth={maxWidth}
        tabIndex={-1}
        error={error}
        {...otherProps}
        onClick={handleSelectClick}
        onKeyDown={handleSelectKeyDown}
        onMouseOver={(event) => { handleMouseOver(event, props.onMouseOver, setIsHovered); }}
        onMouseLeave={(event) => { handleMouseLeave(event, props.onMouseLeave, setIsHovered); }}
        onFocus={(event) => { handleFocus(event, props.onFocus, setClickedOutside, setIsFocused); }}
        onBlur={(event) => { handleBlur(event, props.onBlur, setClickedOutside, setIsFocused); }}
      >
        <SelectItemBasicContext.Provider value={{ ...resolvedBaseProps }} >
          {renderSelectItemBase && renderSelectItemBase()}
        </SelectItemBasicContext.Provider>
      </Styled.SelectItemWrapper>
      {error && errorMessageContent && (
        <Styled.ErrorContainer>
          {error && errorMessageContent}
        </Styled.ErrorContainer>
      )}
      {transitions.map(({ item, key, props: transitionProps }) => item && (
        <Menu key={key} {...resolvedMenuProps} as={animated.div} style={{ ...transitionProps, ...menuStyle }} isOpen>
          <List
            ref={combinedListRefs}
            truncateListText={truncateListText}
            scrollIntoViewOnFocus={false}
            theme={resolvedListTheme}
            style={{
              maxHeight: listMaxHeight,
              minHeight: '2rem',
              paddingTop: '0.25rem',
              ...listStyle,
            }}
            onKeyDown={(event) => { handleListKeyDown(event, onClose, externalKeyDown); }}
            elevationProps={{
              elevationIndex: ELEVATION_INDEX,
              dark: ELEVATION_DARK,
            }}
            hoverEffect
            {...restOfListProps}
          >
            <SelectItemBasicContext.Provider value={{ ...resolvedBaseProps }} >
              {children}
            </SelectItemBasicContext.Provider>
          </List>
        </Menu>
      ))}
    </Fragment>
  );
}));

SelectItemBasic.defaultProps = {
  multiSelect: false,
  isOpen: false,
  variant: 'outlined',
  error: false,
  errorMessage: undefined,
  disabled: undefined, // `disabled`, by default, needs to be undefined to find is it configured by user or not.
  autoFocus: false,
  maxWidth: false,
  truncateListText: true,
  listProps: {},
  menuProps: {},
  onOpen: undefined,
  onClose: undefined,
  animationProps: undefined,
  disableAnimation: undefined,
  // eslint-disable-next-line react/display-name
  renderSelectItemBase: (props: {}) => <SelectItemButton {...props} />,
  style: {},
  onClick: undefined,
  onKeyDown: undefined,
  onMouseOver: undefined,
  onMouseLeave: undefined,
  onFocus: undefined,
  onBlur: undefined,
  theme: {},
};
SelectItemBasic.displayName = 'SelectItemBasic';

export {
  SelectItemBasic as default,
  SelectItemBasicContext
};
